import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { message } from 'antd';
import { getCurrentSession, buildLoginRedirectUrl, invalidateSessionCache } from './auth';

const baseURL = process.env.REACT_APP_KFC_API || 'http://localhost:30080/api/kfc';

// 身份由网关用 Kratos 会话 cookie 校验；X-Requested-With 供网关做 CSRF 校验
export const kfcApi = axios.create({
  baseURL,
  timeout: 10000,
  withCredentials: true,
  headers: { 'X-Requested-With': 'kfy-web' },
});

// 请求拦截器：无会话时跳转登录（页面体验，安全校验在网关）
kfcApi.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    let session = null;
    try {
      session = await getCurrentSession();
    } catch (error) {
      // whoami 发生网络/CORS异常时，不跳转登录，避免 30090 <-> 3001 循环。
      return Promise.reject(error);
    }

    if (!session) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[KFC API] 无会话，将跳转登录。请先通过 Kratos 登录后再访问。');
      }
      window.location.href = buildLoginRedirectUrl();
      return Promise.reject(new Error('未登录，正在跳转到登录页面'));
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器：仅当会话确实失效（whoami 无会话）时才跳转登录，避免 KFC 后端 401 误判为未登录
kfcApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 503) {
      // 网关回查 Kratos 失败，不跳登录以免循环跳转
      message.error({ content: '认证服务暂不可用，请稍后重试', key: 'kfc-auth-unavailable' });
      return Promise.reject(error);
    }
    if (error.response?.status === 401) {
      let session = null;
      try {
        session = await getCurrentSession();
      } catch {
        // whoami 非 401 场景（如 CORS/网络）不触发重定向，避免循环跳转
        return Promise.reject(error);
      }
      if (!session) {
        invalidateSessionCache();
        window.location.href = buildLoginRedirectUrl();
      }
    }
    return Promise.reject(error);
  }
);

export type AgentApiPurpose = 'PRICE' | 'TRACK' | 'LOGISTICS' | 'LABEL';
export type AgentPriceAccessMode = 'REST_API' | 'WEB_PAGE';

export interface AgentCredential {
  name?: string;
  type?: 'API_KEY' | 'ACCOUNT';
  /** 以下按类型二选一：API_KEY 用 appKey/appSecret/coid/nonce/version，ACCOUNT 用 username/password */
  appKey?: string | null;
  appSecret?: string | null;
  username?: string | null;
  password?: string | null;
  coid?: string | null;
  nonce?: string | null;
  version?: string | null;
  remark?: string;
}

/** 与后端 AgentBasicInfo / collection 结构一致 */
export interface AgentDTO {
  id?: string;
  basicInfo?: {
    name?: string;
    identifier?: string;
    /** 凯飞越开户代码：KFY / 实际代码 / 空串（无开户代码） */
    kfyAccountCode?: string;
  };
  contact?: {
    contactPerson?: { name?: string; phone?: string };
    address?: string;
  };
  officialWebsite?: {
    url?: string;
    username?: string;
    password?: string;
  };
  apiConfig?: {
    priceEndPoint?: string;
    trackEndPoint?: string;
    logisticsSystemEndPoint?: string;
    labelEndPoint?: string;
    /** 各端点绑定的凭证名，空视为 default */
    priceCredential?: string;
    trackCredential?: string;
    logisticsCredential?: string;
    labelCredential?: string;
    priceAccessMode?: AgentPriceAccessMode;
    /** 以下为 default 凭证的旧字段镜像，只读兼容 */
    appKey?: string;
    appSecret?: string;
    coid?: string;
    nonce?: string;
    version?: string;
  };
  /** API 凭证列表，必须包含 default */
  credentials?: AgentCredential[];
  bankAccount?: {
    bankName?: string;
    accountName?: string;
    accountNumber?: string;
  };
  /** 代理能力映射：能力名称 -> 对应 API 名称 */
  capabilities?: { capability?: string; apiName?: string }[];
  /** 测量数据来源：API 同步 | MANUAL 手动上传（大模型解析） */
  measurementSource?: 'API' | 'MANUAL';
  createdAt?: string;
}

export interface ChannelDTO {
  id: string;
  agentId: string;
  channelCode: string;
  channelName?: string;
  volumeCoefficient?: number;
  billingRuleId?: string;
  /** 承运商 id；更新时传空串表示清除 */
  carrierId?: string;
  /** 承运商名称（只读） */
  carrierName?: string;
  versionTimestamp?: string;
}

export interface TransportModeDTO {
  id: string;
  mode: string;
  displayName?: string;
  carrierIds?: string[];
  isActive?: boolean;
  createdAt?: string;
}

export interface CarrierDTO {
  id: string;
  carrierCode: string;
  carrierName?: string;
  remark?: string;
  /** 轨迹查询地址模板，{trackingNo} 替换为转单号，{vesselImo}/{vesselMmsi}/{vesselName} 替换为船字段 */
  trackingUrlTemplate?: string;
  /** 代理查价承运商分类：匹配代理渠道名称的关键词 */
  matchKeywords?: string[];
  /** 代理查价承运商分类按钮顺序 */
  sortOrder?: number;
  isActive?: boolean;
  createdAt?: string;
}

/** 起运地/目的地：国家名称、国家编码(ISO 3166-1 alpha-2)、城市名称 */
export interface RouteLocationDTO {
  countryName?: string;
  countryCode?: string;
  cityName?: string;
}

export interface RouteDTO {
  id: string;
  routeCode: string;
  /** D2D（专线）/ P2P（港到港） */
  serviceType?: 'D2D' | 'P2P' | string;
  origin?: RouteLocationDTO;
  destination?: RouteLocationDTO;
  transitTime?: string;
  transportMode?: string;
  /** 承运商 ID */
  carrierId?: string;
  carrierCode?: string;
  carrierName?: string;
  customsProps?: string;
  priceCoefficient?: number;
  isActive?: boolean;
  channelIds?: string[];
}

export interface ReceivingRecordDTO {
  id: string;
  trackingNumber: string;
  source?: string;
  supplier?: string;
  preDeclarationId?: string;
  shipmentId?: string;
  measurementJson?: string;
  trackingDetailsJson?: string;
  createdAt?: string;
}

export interface PreDeclarationDTO {
  id: string;
  preDeclarationCode: string;
  clientCode: string;
  clientAddr?: string;
  routeId: string;
  status: string;
  expectedShipDate?: string;
  cargoDescJson?: string;
  customsDeclJson?: string;
  shipmentId?: string;
  createdBy?: string;
  createdAt?: string;
  receivingRecords?: ReceivingRecordDTO[];
}

export interface ShipmentInvoiceDTO {
  id: string;
  shipmentId: string;
  fileName: string;
  generatedAt: string;
  /** UPLOADED | GENERATED */
  source?: string;
  uploadedBy?: string;
  contentType?: string;
  /** UNCONFIRMED | VALID | INVALID */
  validity?: string;
  confirmedAt?: string;
  confirmedBy?: string;
}

export interface ShipmentCustomsDocDTO {
  id: string;
  shipmentId: string;
  fileName: string;
  contentType?: string;
  sizeBytes?: number;
  uploadedBy?: string;
  createdAt?: string;
}

export interface ShipmentProblemLogDTO {
  id: string;
  shipmentId: string;
  content: string;
  createdBy?: string;
  createdAt?: string;
  relatedAction?: string;
}

export interface ShipmentWeightConfirmationDTO {
  id: string;
  shipmentId: string;
  confirmedWeight?: number;
  beforeConfirmedWeight?: number;
  pendingWeight?: number;
  suggestedKg?: number;
  billingRuleName?: string;
  beforeStatus?: string;
  afterStatus?: string;
  evidenceText?: string;
  images?: { fileName: string; contentType?: string; sizeBytes?: number }[];
  confirmedAt?: string;
  confirmedBy?: string;
  confirmedByName?: string;
}

export interface ShipmentDTO {
  shipmentId: string;
  /** 预申报单 ID，用于绑定货物（仅预申报状态时有值） */
  preDeclarationId?: string;
  clientCode?: string;
  clientAddr?: string;
  routeId?: string;
  routeName?: string;
  /** 目的地国家（只读展示名） */
  destinationCountry?: string;
  /** 目的国家编码（ISO 3166-1 alpha-2） */
  destinationCountryCode?: string;
  /** 目的国家名称 */
  destinationCountryName?: string;
  /** 落库箱数（无绑箱时）；展示请用 cartonCount */
  declaredPieceCount?: number;
  /** 代理 ID */
  agentId?: string;
  /** 代理标识（只读展示） */
  agentIdentifier?: string;
  /** 服务类型：D2D / P2P */
  serviceType?: string;
  /** 运输方式：AIR / SEA / EXPRESS / … */
  transportMode?: string;
  /** 业务渠道 ID */
  channelId?: string;
  channelCode?: string;
  channelName?: string;
  channelShipmentId?: string;
  /** 渠道关联的承运商 */
  carrierId?: string;
  carrierCode?: string;
  carrierName?: string;
  /** 转单号轨迹查询链接 */
  channelShipmentTrackingUrl?: string;
  /** 有转单号但无链接时的原因 */
  channelShipmentTrackingHint?: string;
  /** 代理入库单号（交代理仓后由代理 API 返回） */
  agentInboundNo?: string;
  /** 代理订单来源：CREATED_IN_KFY / SYNCED_FROM_AGENT */
  agentOrderOrigin?: string;
  /** 代理入库状态（0新增,1已下单,2收货中,3已收货,5已确认,7调拨中,8扣货等） */
  agentInboundStatus?: number;
  /** 代理出库状态（0待出库,1出库中,2已出库） */
  agentOutboundStatus?: number;
  /** 代理测量数据（JSON 字符串） */
  agentMeasurementDataJson?: string;
  /** 代理测量重量（毛重，kg） */
  agentMeasuredWeightKg?: number;
  /** 代理测量体积（m³） */
  agentMeasuredVolumeCbm?: number;
  /** 关联代理的测量数据来源配置：MANUAL 时详情可上传测量数据 */
  agentMeasurementMode?: 'API' | 'MANUAL';
  /** 当前测量数据来源 */
  agentMeasurementSource?: 'API' | 'MANUAL_LLM';
  manualMeasurementRecordId?: string;
  manualMeasurementConfirmedAt?: string;
  manualMeasurementConfirmedBy?: string;
  /** 箱数不一致待处理：详情「绑定的货物」区展示三选一 */
  cargoSyncPending?: boolean;
  cargoSyncKfyCount?: number;
  cargoSyncAgentCount?: number;
  /** 最新轨迹展示（物流格式、按时间排序） */
  agentTrackingLatest?: { time?: string; location?: string; description?: string }[];
  status: string;
  expectedShipDate?: string;
  cargoDescJson?: string;
  customsDeclJson?: string;
  /** 代理计费重（确认重量） */
  confirmedWeight?: number;
  pendingWeight?: number;
  billingCalcSuggestedKg?: number;
  billingCalcProcessJson?: string;
  billingCalcRuleId?: string;
  billingCalcFormulaVersion?: number;
  billingCalcStale?: boolean;
  /** 过期原因：RULE_CHANGED / MEASUREMENT_CHANGED */
  billingCalcStaleReason?: string;
  billingCalcMissingInput?: boolean;
  billingCalcAt?: string;
  billingRuleName?: string;
  billingRuleText?: string;
  hasBillingRule?: boolean;
  /** 货物箱数（有绑箱=聚合数，无绑箱=落库值） */
  cartonCount?: number;
  /** 实际绑定箱数（>0 时不可直接改货物箱数） */
  boundCartonCount?: number;
  /** 已绑定批次数（>0 时不可直接改货物箱数） */
  boundBatchCount?: number;
  /** 运单备注（可选） */
  remark?: string;
  /** 是否需要报关（默认 true） */
  requiresCustoms?: boolean;
  /** 是否带电（默认 false） */
  containsBattery?: boolean;
  /** 报关资料份数 */
  customsDocCount?: number;
  /** 当前是否为问题件 */
  problemActive?: boolean;
  /** 问题件业务状态：OPEN / IN_PROGRESS / RESOLVED */
  problemStatus?: string;
  problemMarkedAt?: string;
  problemResolvedAt?: string;
  /** LLM | MANUAL */
  problemMarkedSource?: string;
  /** 问题件进展日志条数 */
  problemLogCount?: number;
  createdAt?: string;
  updatedAt?: string;
  /** 进入当前状态时刻 */
  statusChangedAt?: string;
  /** 首次签收时刻 */
  deliveredAt?: string;
  manualDeliveryConfirmedAt?: string;
  manualDeliveryConfirmedBy?: string;
  manualDeliveryNote?: string;
  /** 是否允许手动更新轨迹 */
  manualTrackingEnabled?: boolean;
  /** 是否已有手动轨迹事件 */
  hasManualTrackEvents?: boolean;
  /** 船名（海运，如 LURLINE） */
  vesselName?: string;
  /** 航次（如 104E） */
  vesselVoyage?: string;
  vesselImo?: string;
  vesselMmsi?: string;
  cancelledAt?: string;
  cancelledBy?: string;
  cancelReason?: string;
  receivingRecords?: ReceivingRecordDTO[];
}

export const agentApi = {
  list: () => kfcApi.get('/agents') as Promise<AxiosResponse<AgentDTO[]>>,
  get: (id: string) => kfcApi.get(`/agents/${id}`) as Promise<AxiosResponse<AgentDTO>>,
  create: (dto: Partial<AgentDTO>) => kfcApi.post('/agents', dto) as Promise<AxiosResponse<AgentDTO>>,
  update: (id: string, dto: Partial<AgentDTO>) => kfcApi.put(`/agents/${id}`, dto) as Promise<AxiosResponse<AgentDTO>>,
  delete: (id: string) => kfcApi.delete(`/agents/${id}`),
  testConnection: (id: string, purpose?: AgentApiPurpose) =>
    kfcApi.post(`/agents/${id}/test-connection`, null, { params: purpose ? { purpose } : undefined }) as Promise<AxiosResponse<{ success: boolean }>>,
};

export const channelApi = {
  list: (agentId?: string) => kfcApi.get('/channels', { params: agentId ? { agentId } : {} }) as Promise<AxiosResponse<ChannelDTO[]>>,
  get: (id: string) => kfcApi.get(`/channels/${id}`) as Promise<AxiosResponse<ChannelDTO>>,
  save: (dto: Partial<ChannelDTO>) => kfcApi.post('/channels', dto) as Promise<AxiosResponse<ChannelDTO>>,
  update: (id: string, dto: Partial<ChannelDTO>) => kfcApi.put(`/channels/${id}`, dto) as Promise<AxiosResponse<ChannelDTO>>,
  delete: (id: string) => kfcApi.delete(`/channels/${id}`),
};

export const transportModeApi = {
  list: (activeOnly?: boolean) =>
    kfcApi.get('/transport-modes', { params: activeOnly ? { activeOnly: true } : {} }) as Promise<
      AxiosResponse<TransportModeDTO[]>
    >,
  get: (id: string) => kfcApi.get(`/transport-modes/${id}`) as Promise<AxiosResponse<TransportModeDTO>>,
  create: (dto: Partial<TransportModeDTO>) =>
    kfcApi.post('/transport-modes', dto) as Promise<AxiosResponse<TransportModeDTO>>,
  update: (id: string, dto: Partial<TransportModeDTO>) =>
    kfcApi.put(`/transport-modes/${id}`, dto) as Promise<AxiosResponse<TransportModeDTO>>,
  delete: (id: string) => kfcApi.delete(`/transport-modes/${id}`),
};

export const carrierApi = {
  list: (activeOnly?: boolean) =>
    kfcApi.get('/carriers', { params: activeOnly ? { activeOnly: true } : {} }) as Promise<
      AxiosResponse<CarrierDTO[]>
    >,
  get: (id: string) => kfcApi.get(`/carriers/${id}`) as Promise<AxiosResponse<CarrierDTO>>,
  create: (dto: Partial<CarrierDTO>) =>
    kfcApi.post('/carriers', dto) as Promise<AxiosResponse<CarrierDTO>>,
  update: (id: string, dto: Partial<CarrierDTO>) =>
    kfcApi.put(`/carriers/${id}`, dto) as Promise<AxiosResponse<CarrierDTO>>,
  delete: (id: string) => kfcApi.delete(`/carriers/${id}`),
};

export const routeApi = {
  list: (activeOnly?: boolean) => kfcApi.get('/routes', { params: activeOnly ? { activeOnly: true } : {} }) as Promise<AxiosResponse<RouteDTO[]>>,
  get: (id: string) => kfcApi.get(`/routes/${id}`) as Promise<AxiosResponse<RouteDTO>>,
  create: (dto: Partial<RouteDTO>) => kfcApi.post('/routes', dto) as Promise<AxiosResponse<RouteDTO>>,
  update: (id: string, dto: Partial<RouteDTO>) => kfcApi.put(`/routes/${id}`, dto) as Promise<AxiosResponse<RouteDTO>>,
};

export const preDeclarationApi = {
  list: () => kfcApi.get('/pre-declarations') as Promise<AxiosResponse<PreDeclarationDTO[]>>,
  get: (id: string) => kfcApi.get(`/pre-declarations/${id}`) as Promise<AxiosResponse<PreDeclarationDTO>>,
  create: (dto: {
    clientCode: string;
    clientAddr?: string;
    transportMode: string;
    channelId?: string;
    agentId?: string;
    destinationCountryCode: string;
    destinationCountryName?: string;
    serviceType?: string;
    routeCode?: string;
    shipmentId?: string;
    expectedShipDate?: string;
    cargoDescJson: string;
    customsDeclJson?: string;
    declaredPieceCount: number;
    remark?: string;
    requiresCustoms?: boolean;
    containsBattery?: boolean;
  }) => kfcApi.post('/pre-declarations', dto) as Promise<AxiosResponse<PreDeclarationDTO>>,
  bindReceiving: (id: string, receivingRecordIds: string[]) =>
    kfcApi.post(`/pre-declarations/${id}/bind-receiving`, { preDeclarationId: id, receivingRecordIds }),
};

export interface ShipmentStatusCountsDTO {
  /** 状态码 → 运单数（历史状态已归并） */
  counts: Record<string, number>;
  /** 「全部」显示数：总单量减去已撤销 */
  totalExcludingCancelled: number;
}

export const shipmentApi = {
  list: (params?: {
    clientCode?: string;
    status?: string;
    shipmentIdKeyword?: string;
    problemFilter?: 'ALL' | 'ACTIVE' | 'INACTIVE';
    agentName?: string;
    page?: number;
    size?: number;
  }) =>
    kfcApi.get('/shipments', { params: params || {} }) as Promise<AxiosResponse<{ content: ShipmentDTO[]; totalElements: number }>>,
  /** 运单状态计数（列表顶部手风琴）：全局统计，不跟随筛选条件 */
  statusCounts: () =>
    kfcApi.get('/shipments/status-counts') as Promise<AxiosResponse<ShipmentStatusCountsDTO>>,
  get: (shipmentId: string) => kfcApi.get(`/shipments/${shipmentId}`) as Promise<AxiosResponse<ShipmentDTO>>,
  handoverConfirm: (dto: { shipmentId: string; pendingWeight?: number; handoverProofJson?: string; deliveryTrackingNo?: string }) =>
    kfcApi.post('/shipments/handover', dto),
  /** 确认重量并附带凭证（evidenceText / images 选填），返回本次确认记录 */
  weightConfirmWithEvidence: (shipmentId: string, form: FormData) =>
    kfcApi.post(`/shipments/${shipmentId}/weight-confirm`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    }) as Promise<AxiosResponse<ShipmentWeightConfirmationDTO>>,
  listWeightConfirmations: (shipmentId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/weight-confirmations`) as Promise<AxiosResponse<ShipmentWeightConfirmationDTO[]>>,
  getWeightConfirmationImage: (shipmentId: string, recordId: string, index: number) =>
    kfcApi.get(`/shipments/${shipmentId}/weight-confirmations/${recordId}/images/${index}`, {
      responseType: 'blob',
      timeout: 30000,
    }) as Promise<AxiosResponse<Blob>>,
  weightConfirm: (dto: { shipmentId: string; confirmedWeight?: number; accept?: boolean }) =>
    kfcApi.post('/shipments/weight-confirm', dto),
  calculateBillingWeight: (shipmentId: string) =>
    kfcApi.post(`/shipments/${shipmentId}/calculate-billing-weight`) as Promise<
      AxiosResponse<BillingWeightCalcResultDTO>
    >,
  setChannelShipmentId: (shipmentId: string, channelShipmentId: string) =>
    kfcApi.put(`/shipments/${shipmentId}/channel-shipment-id`, { channelShipmentId }),
  updateRoute: (shipmentId: string, routeCode: string) =>
    kfcApi.put(`/shipments/${shipmentId}/route`, { routeCode }),
  updateLogistics: (
    shipmentId: string,
    body: {
      transportMode: string;
      channelId?: string | null;
      agentId?: string;
      destinationCountryCode?: string;
      destinationCountryName?: string;
      serviceType?: string;
      routeCode?: string | null;
    }
  ) => kfcApi.put(`/shipments/${shipmentId}/logistics`, body),
  updateRemark: (shipmentId: string, remark: string) =>
    kfcApi.put(`/shipments/${shipmentId}/remark`, { remark }),
  /** 修改运单地址：仅预申报；仅允许选择该客户地址簿条目 */
  updateAddress: (shipmentId: string, customerAddressId: string) =>
    kfcApi.put(`/shipments/${shipmentId}/address`, { customerAddressId }),
  updateDestinationCountry: (
    shipmentId: string,
    body: { destinationCountryCode: string; destinationCountryName?: string }
  ) => kfcApi.put(`/shipments/${shipmentId}/destination-country`, body),
  updatePieceCount: (shipmentId: string, declaredPieceCount: number) =>
    kfcApi.put(`/shipments/${shipmentId}/piece-count`, { declaredPieceCount }),
  updateRequiresCustoms: (shipmentId: string, requiresCustoms: boolean) =>
    kfcApi.put(`/shipments/${shipmentId}/requires-customs`, { requiresCustoms }),
  updateContainsBattery: (shipmentId: string, containsBattery: boolean) =>
    kfcApi.put(`/shipments/${shipmentId}/contains-battery`, { containsBattery }),
  listCustomsDocs: (shipmentId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/customs-docs`) as Promise<AxiosResponse<ShipmentCustomsDocDTO[]>>,
  uploadCustomsDoc: (shipmentId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return kfcApi.post(`/shipments/${shipmentId}/customs-docs`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }) as Promise<AxiosResponse<ShipmentCustomsDocDTO>>;
  },
  downloadCustomsDoc: (shipmentId: string, docId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/customs-docs/${docId}/download`, {
      responseType: 'blob',
    }) as Promise<AxiosResponse<Blob>>,
  deleteCustomsDoc: (shipmentId: string, docId: string) =>
    kfcApi.delete(`/shipments/${shipmentId}/customs-docs/${docId}`),
  markProblem: (shipmentId: string, content: string) =>
    kfcApi.post(`/shipments/${shipmentId}/mark-problem`, { content }),
  updateProblemStatus: (shipmentId: string, body: { problemStatus: 'OPEN' | 'IN_PROGRESS'; content?: string }) =>
    kfcApi.put(`/shipments/${shipmentId}/problem-status`, body),
  listProblemLogs: (shipmentId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/problem-logs`) as Promise<AxiosResponse<ShipmentProblemLogDTO[]>>,
  addProblemLog: (shipmentId: string, content: string) =>
    kfcApi.post(`/shipments/${shipmentId}/problem-logs`, { content }) as Promise<AxiosResponse<ShipmentProblemLogDTO>>,
  resolveProblem: (shipmentId: string, body?: { content?: string; resolveReason?: 'REAL' | 'FALSE_POSITIVE' }) =>
    kfcApi.post(`/shipments/${shipmentId}/resolve-problem`, body || {}),
  getSubmitDepotContext: (shipmentId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/submit-depot-context`) as Promise<AxiosResponse<SubmitDepotContextDTO>>,
  checkAgentOrder: (shipmentId: string, params?: { agentInboundNo?: string }) =>
    kfcApi.get(`/shipments/${shipmentId}/check-agent-order`, {
      params: params?.agentInboundNo ? { agentInboundNo: params.agentInboundNo } : undefined,
    }) as Promise<AxiosResponse<CheckAgentOrderResultDTO>>,
  submitToAgentDepot: (shipmentId: string, body?: SubmitToDepotRequest) =>
    kfcApi.post(`/shipments/${shipmentId}/submit-depot`, body || {}) as Promise<AxiosResponse<SubmitToDepotResultDTO>>,
  getAgentLabel: (shipmentId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/agent-label`) as Promise<AxiosResponse<AgentLabelResult>>,
  cancelAgentOrder: (shipmentId: string) =>
    kfcApi.post(`/shipments/${shipmentId}/cancel-agent-order`) as Promise<AxiosResponse<CancelOrderResult>>,
  /** 撤销运单（仅预申报），自动解绑全部货物 */
  cancelShipment: (shipmentId: string, reason?: string) =>
    kfcApi.post(`/shipments/${shipmentId}/cancel`, { reason }) as Promise<AxiosResponse<ShipmentDTO>>,
  /** 重新申报（已撤销 → 预申报） */
  redeclareShipment: (shipmentId: string) =>
    kfcApi.post(`/shipments/${shipmentId}/redeclare`) as Promise<AxiosResponse<ShipmentDTO>>,
  submitInvoice: (shipmentId: string) =>
    kfcApi.post(`/shipments/${shipmentId}/submit-invoice`),
  /** 生成发票 Excel，返回发票记录（后端保留；主 UI 不调用） */
  generateInvoice: (shipmentId: string) =>
    kfcApi.post(`/shipments/${shipmentId}/generate-invoice`) as Promise<AxiosResponse<ShipmentInvoiceDTO>>,
  /** 手动上传发票 */
  uploadInvoice: (shipmentId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return kfcApi.post(`/shipments/${shipmentId}/invoices/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }) as Promise<AxiosResponse<ShipmentInvoiceDTO>>;
  },
  /** 确认为有效发票 */
  confirmValidInvoice: (shipmentId: string, invoiceId: string) =>
    kfcApi.post(`/shipments/${shipmentId}/invoices/${invoiceId}/confirm-valid`) as Promise<
      AxiosResponse<ShipmentInvoiceDTO>
    >,
  /** 生成运单级随货清单 */
  generatePackingList: (shipmentId: string) =>
    kfcApi.post(`/shipments/${shipmentId}/packing-lists`) as Promise<AxiosResponse<ShipmentPackingListDTO>>,
  /** 生成批次级随货清单 */
  generateBatchPackingList: (shipmentId: string, batchId: string) =>
    kfcApi.post(`/shipments/${shipmentId}/packing-lists/by-batch`, null, {
      params: { batchId },
    }) as Promise<AxiosResponse<ShipmentPackingListDTO>>,
  listPackingLists: (shipmentId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/packing-lists`) as Promise<AxiosResponse<ShipmentPackingListDTO[]>>,
  downloadPackingList: (shipmentId: string, recordId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/packing-lists/${recordId}/download`, {
      responseType: 'blob',
    }) as Promise<AxiosResponse<Blob>>,
  /** 运单下的发票记录列表 */
  listInvoices: (shipmentId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/invoices`) as Promise<AxiosResponse<ShipmentInvoiceDTO[]>>,
  /** 下载发票文件（返回 blob） */
  downloadInvoice: (shipmentId: string, invoiceId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/invoices/${invoiceId}/download`, { responseType: 'blob' }) as Promise<AxiosResponse<Blob>>,
  /** 同步轨迹（拉代理轨迹并按推断前向推进状态） */
  syncTracking: (shipmentId: string) =>
    kfcApi.post(`/shipments/${shipmentId}/sync-tracking`) as Promise<AxiosResponse<SyncTrackingDataResultDTO>>,
  /** 同步代理入仓数据（入库/出库/测量，仅当有代理入库单号时可操作） */
  syncAgentData: (shipmentId: string) =>
    kfcApi.post(`/shipments/${shipmentId}/sync-agent-data`) as Promise<AxiosResponse<SyncAgentDataResultDTO>>,
  /** 详情区处理绑定箱数与代理测量箱数不一致 */
  resolveCargoSync: (shipmentId: string, body: { mode: 'REPLACE' | 'MERGE' | 'LATER' }) =>
    kfcApi.post(`/shipments/${shipmentId}/resolve-cargo-sync`, body) as Promise<AxiosResponse<ResolveCargoSyncResultDTO>>,
  /** 代理测量箱匹配到运单已绑定批次并生成单箱 */
  previewAgentCargoMatch: (shipmentId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/agent-cargo-match-preview`) as Promise<AxiosResponse<SyncAgentDataResultDTO>>,
  matchAgentCargo: (shipmentId: string, body: MatchAgentCargoRequest) =>
    kfcApi.post(`/shipments/${shipmentId}/match-agent-cargo`, body) as Promise<AxiosResponse<MatchAgentCargoResultDTO>>,
  /** 手动测量：上传记录列表 */
  listManualMeasurements: (shipmentId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/manual-measurements`) as Promise<AxiosResponse<ManualMeasurementRecordDTO[]>>,
  getManualMeasurement: (shipmentId: string, recordId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/manual-measurements/${recordId}`) as Promise<
      AxiosResponse<ManualMeasurementRecordDTO>
    >,
  /** 手动测量：提交文字与图片，后端异步调用大模型，返回 PARSING 记录 */
  parseManualMeasurement: (shipmentId: string, text: string, images: File[]) => {
    const form = new FormData();
    if (text.trim()) form.append('text', text);
    images.forEach((f) => form.append('images', f, f.name));
    return kfcApi.post(`/shipments/${shipmentId}/manual-measurements/parse`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    }) as Promise<AxiosResponse<ManualMeasurementRecordDTO>>;
  },
  confirmManualMeasurement: (
    shipmentId: string,
    recordId: string,
    body: { items: ManualMeasurementItem[]; totals?: ManualMeasurementTotals },
  ) =>
    kfcApi.post(`/shipments/${shipmentId}/manual-measurements/${recordId}/confirm`, body, {
      timeout: 60000,
    }) as Promise<AxiosResponse<ManualMeasurementConfirmResultDTO>>,
  /** 手动测量：未经模型解析的纯手工确认 */
  confirmManualMeasurementDirect: (
    shipmentId: string,
    text: string,
    images: File[],
    items: ManualMeasurementItem[],
    totals?: ManualMeasurementTotals,
  ) => {
    const form = new FormData();
    if (text.trim()) form.append('text', text);
    images.forEach((f) => form.append('images', f, f.name));
    form.append('items', JSON.stringify(items));
    if (totals) form.append('totals', JSON.stringify(totals));
    return kfcApi.post(`/shipments/${shipmentId}/manual-measurements/manual`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    }) as Promise<AxiosResponse<ManualMeasurementConfirmResultDTO>>;
  },
  getManualMeasurementImage: (shipmentId: string, recordId: string, index: number) =>
    kfcApi.get(`/shipments/${shipmentId}/manual-measurements/${recordId}/images/${index}`, {
      responseType: 'blob',
      timeout: 30000,
    }) as Promise<AxiosResponse<Blob>>,
  /** 同步轨迹（与 syncTracking 同义，兼容旧路径） */
  syncTrackingData: (shipmentId: string) =>
    kfcApi.post(`/shipments/${shipmentId}/sync-tracking-data`) as Promise<AxiosResponse<SyncTrackingDataResultDTO>>,
  /** 获取轨迹展示数据（代理原始或兼容结构），用于时间线等富展示 */
  getTrackingDisplay: (shipmentId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/tracking-display`) as Promise<AxiosResponse<TrackingDisplayDTO>>,
  manualConfirmDelivery: (shipmentId: string, form: FormData) =>
    kfcApi.post(`/shipments/${shipmentId}/manual-confirm-delivery`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    }) as Promise<AxiosResponse<ManualConfirmDeliveryResultDTO>>,
  getManualDeliveryEvidence: (shipmentId: string, fileId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/manual-delivery-evidence/${fileId}`, {
      responseType: 'blob',
      timeout: 30000,
    }) as Promise<AxiosResponse<Blob>>,
  setManualTrackingEnabled: (shipmentId: string, enabled: boolean) =>
    kfcApi.put(`/shipments/${shipmentId}/manual-tracking-enabled`, { enabled }) as Promise<AxiosResponse<ShipmentDTO>>,
  addManualTrackEvent: (shipmentId: string, form: FormData) =>
    kfcApi.post(`/shipments/${shipmentId}/manual-track-events`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    }) as Promise<AxiosResponse<ManualTrackEventResultDTO>>,
  getManualTrackEvidence: (shipmentId: string, eventId: string, fileId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/manual-track-evidence/${eventId}/${fileId}`, {
      responseType: 'blob',
      timeout: 30000,
    }) as Promise<AxiosResponse<Blob>>,
};

export interface ManualTrackEventResultDTO {
  shipmentId: string;
  eventId?: string;
  previousStatus?: string;
  newStatus?: string;
  statusAdvanced?: boolean;
  inferredStatus?: string | null;
}

export interface ManualConfirmDeliveryResultDTO {
  shipmentId: string;
  previousStatus?: string;
  newStatus?: string;
  deliveredAt?: string;
  note?: string;
  fileCount?: number;
  problemResolved?: boolean;
  confirmedBy?: string;
  confirmedByName?: string;
}

/** 轨迹展示数据结构（兼容不同代理：56yorky 等含 trackInfoList/inputChanges） */
export interface TrackingDisplayDTO {
  serviceName?: string;
  userNo?: string;
  latestTime?: string;
  latestStatus?: string;
  latestDetail?: string;
  trackInfoList?: TrackInfoItemDTO[];
  inputChanges?: InputChangeItemDTO[];
  /** 停留摘要（016 §11，服务端 enrich） */
  summary?: TrackingDwellSummaryDTO;
}

export interface TrackingDwellSummaryDTO {
  segmentCount?: number;
  overCount?: number;
  maxDwellDays?: number;
  maxDwellLabel?: string;
}

export interface TrackInfoItemDTO {
  trackTime?: string;
  trackStation?: string;
  trackStationEn?: string;
  trackDesc?: string;
  trackDescEn?: string;
  trackStatusText?: string;
  /** 进入时刻 */
  enteredAt?: string;
  /** 离开时刻；进行中为 null */
  leftAt?: string | null;
  dwellDays?: number | null;
  mappedStatus?: string | null;
  thresholdDays?: number | null;
  overThreshold?: boolean;
  ongoing?: boolean;
  /** 纯国家名上的清关/费用单证，标题已改为「清关单证」，不展示地点和停留 */
  documentLocation?: boolean;
  source?: string;
  evidenceFiles?: { id: string; name?: string; url?: string; eventId?: string }[];
  [key: string]: unknown;
}

export interface InputChangeItemDTO {
  createTime?: string;
  busiCode?: string;
  remark?: string;
  [key: string]: unknown;
}

export const receivingRecordApi = {
  listUnbound: () => kfcApi.get('/receiving-records/unbound') as Promise<AxiosResponse<ReceivingRecordDTO[]>>,
  listByPreDeclaration: (preDeclarationId: string) =>
    kfcApi.get(`/receiving-records/by-pre-declaration/${preDeclarationId}`) as Promise<AxiosResponse<ReceivingRecordDTO[]>>,
  create: (dto: { trackingNumber: string; source?: string; supplier?: string; measurementJson?: string }) =>
    kfcApi.post('/receiving-records', dto) as Promise<AxiosResponse<ReceivingRecordDTO>>,
};

export const channelSyncApi = {
  syncFromAgent: (agentId: string) =>
    kfcApi.post(`/channel-sync/agents/${agentId}`) as Promise<AxiosResponse<{ success: boolean; message: string; channels: ChannelDTO[] }>>,
};

export interface AgentDepotDTO {
  id?: string;
  agentId?: string;
  depotId?: string;
  depotName?: string;
  depotAddr?: string;
  depotContact?: string;
  /** AUTO=代理API同步；MANUAL=手动添加 */
  source?: 'AUTO' | 'MANUAL' | string;
}

/** 交代理仓请求：选择仓库、货拉拉单号、照片等；或同步代理已有运单 */
export interface SubmitToDepotRequest {
  depotId?: string;
  huolalaOrderNo?: string;
  photoUrls?: string[];
  /** 为 true 时同步代理已有运单，不 createOrder */
  syncExisting?: boolean;
  /** 查询未命中时仍强制用运单号绑定（哲盟手动号） */
  forceBind?: boolean;
  /** 哲盟 Q5 创建订单重量 kg */
  weig?: number;
  /** 哲盟 Q5 服务渠道编码 hubInCode */
  hubInCode?: string;
  /** 同步已有运单时是否创建货物，默认 true */
  syncCargo?: boolean;
  /** 勾选同步的货物 sourceIndex；省略且 syncCargo=true 表示全选 */
  selectedCargoIndexes?: number[];
  /** track 未命中时用入仓单号经 depot/info 校验后绑定（56yorky） */
  agentInboundNo?: string;
  /** 运单已绑定批次时：代理箱归属批次；省略则后端按批次剩余件数自动分配 */
  cargoBatchAssignments?: CargoBatchAssignment[];
  /** 匹配后是否按实际单箱数更新批次件数，默认 true */
  updateBatchPieceCount?: boolean;
  /** 需要报关但未上传资料时，二次确认后传 true */
  confirmWithoutCustomsDocs?: boolean;
}

/** 代理货物预览项 */
export interface AgentCargoItemDTO {
  sourceIndex?: number;
  cartonId?: string;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
  measuredWeightKg?: number;
  billingWeightKg?: number;
  volumeCbm?: number;
  locationCode?: string;
  /** 本运单已存在同箱号单箱时为其 id（已生成） */
  existingCartonId?: string;
  existingBatchId?: string;
}

/** 代理箱（sourceIndex）归属批次（batchId 批次标识） */
export interface CargoBatchAssignment {
  sourceIndex: number;
  batchId: string;
}

export interface MatchAgentCargoRequest {
  assignments?: CargoBatchAssignment[];
  updateBatchPieceCount?: boolean;
}

export interface MatchAgentCargoResultDTO {
  success?: boolean;
  message?: string;
  createdCartons?: string[];
  updatedCartons?: string[];
  warnings?: string[];
  batches?: CartonBatchDTO[];
  cargoSyncPending?: boolean;
}

/** 同步代理入仓数据结果 */
export interface SyncAgentDataResultDTO {
  statusSynced?: boolean;
  cargoSyncPending?: boolean;
  kfyCartonCount?: number;
  agentCargoCount?: number;
  message?: string;
  cargoItems?: AgentCargoItemDTO[];
  /** 运单已绑定批次；非空时用「匹配到批次」处理箱数不一致 */
  boundBatches?: CartonBatchDTO[];
}

/** 手动测量：单箱数据（cm / kg / m³） */
export interface ManualMeasurementItem {
  cartonId?: string | null;
  lengthCm?: number | null;
  widthCm?: number | null;
  heightCm?: number | null;
  grossWeightKg?: number | null;
  volumeWeightKg?: number | null;
  volumeCbm?: number | null;
}

export interface ManualMeasurementTotals {
  grossWeightKg?: number | null;
  volumeCbm?: number | null;
}

export interface ManualMeasurementRecordDTO {
  id: string;
  shipmentId: string;
  agentIdentifier?: string;
  text?: string;
  images?: { index: number; fileName: string; contentType?: string; sizeBytes?: number }[];
  llmProviderName?: string;
  llmModel?: string;
  llmReasoningEffort?: string;
  promptTokens?: number;
  completionTokens?: number;
  parsedItems?: ManualMeasurementItem[];
  parsedTotals?: ManualMeasurementTotals;
  conflicts?: { field: string; textValue?: string; imageValue?: string; note?: string }[];
  notes?: string;
  parseError?: string;
  parseDurationMs?: number;
  status: 'PARSING' | 'PARSED' | 'CONFIRMED' | 'SUPERSEDED';
  manualOnly?: boolean;
  confirmedItems?: ManualMeasurementItem[];
  confirmedTotals?: ManualMeasurementTotals;
  confirmedAt?: string;
  confirmedBy?: string;
  confirmedByName?: string;
  createdAt?: string;
  createdBy?: string;
  createdByName?: string;
  boundCartonIds?: string[];
}

export interface ManualMeasurementConfirmResultDTO extends SyncAgentDataResultDTO {
  recordId?: string;
  billingSuggestedKg?: number;
  billingMessage?: string;
}

/** 同步轨迹结果（拉轨迹 + 可选状态前向推进） */
export interface SyncTrackingDataResultDTO {
  trackingUpdated?: boolean;
  statusAdvanced?: boolean;
  previousStatus?: string;
  newStatus?: string;
  inferredStatus?: string;
  message?: string;
}

export interface ResolveCargoSyncResultDTO {
  success?: boolean;
  message?: string;
  mode?: string;
  createdCartons?: string[];
  unboundCartons?: string[];
  reboundCartons?: string[];
  warnings?: string[];
  cargoSyncPending?: boolean;
}

export interface SubmitToDepotResultDTO {
  success?: boolean;
  message?: string;
  agentInboundNo?: string;
  agentOrderOrigin?: string;
  createdCartons?: string[];
  warnings?: string[];
}

export interface AgentLabelResult {
  success?: boolean;
  message?: string;
  labelUrl?: string;
  invoiceUrl?: string;
  agentInboundNo?: string;
}

export interface CancelOrderResult {
  success?: boolean;
  message?: string;
}

/** 交代理仓前：查代理是否已有运单及对比信息 */
export interface CheckAgentOrderResultDTO {
  exists: boolean;
  agentInboundNo?: string;
  message?: string;
  agentIdentifier?: string;
  kfy?: {
    shipmentId?: string;
    clientCode?: string;
    routeName?: string;
    cartonCount?: number;
    declaredPieceCount?: number;
    effectivePieceCount?: number;
    status?: string;
  };
  agent?: {
    userNo?: string;
    inputId?: string;
    serviceName?: string;
    trackSummary?: string;
    raw?: unknown;
  };
  cargoItems?: AgentCargoItemDTO[];
  /** 运单已绑定批次（代理箱可选归属批次） */
  boundBatches?: CartonBatchDTO[];
  cargoFetchMessage?: string;
  /** 56yorky：需手动填入仓单号再校验 */
  needManualInboundNo?: boolean;
}

/** 交代理仓弹窗上下文：代理信息 + 代理仓库列表 */
export interface SubmitDepotContextDTO {
  agentId: string;
  agent: AgentDTO | null;
  depots: AgentDepotDTO[];
  serviceChannels?: AgentServiceChannelDTO[];
  suggestedWeig?: number;
  weigRequiredForCreate?: boolean;
}

export interface AgentServiceChannelDTO {
  id?: string;
  agentId?: string;
  serviceId?: number;
  serviceName?: string;
  /** 外部渠道编码（哲盟 Q5 hubInId/code） */
  serviceCode?: string;
  serviceStatus?: number;
  country?: string;
  stype?: string;
}

export const agentDepotApi = {
  list: (agentId?: string) =>
    kfcApi.get('/agent-depots', { params: agentId ? { agentId } : {} }) as Promise<AxiosResponse<AgentDepotDTO[]>>,
  create: (body: AgentDepotDTO) =>
    kfcApi.post('/agent-depots', body) as Promise<AxiosResponse<AgentDepotDTO>>,
  remove: (id: string) => kfcApi.delete(`/agent-depots/${id}`),
  syncFromAgent: (agentId: string) =>
    kfcApi.post(`/agent-depots/sync/${agentId}`) as Promise<AxiosResponse<{ success: boolean; message: string; depots: AgentDepotDTO[] }>>,
};

export const agentServiceApi = {
  list: (agentId?: string) =>
    kfcApi.get('/agent-services', { params: agentId ? { agentId } : {} }) as Promise<AxiosResponse<AgentServiceChannelDTO[]>>,
  syncFromAgent: (agentId: string) =>
    kfcApi.post(`/agent-services/sync/${agentId}`) as Promise<AxiosResponse<{ success: boolean; message: string; services: AgentServiceChannelDTO[] }>>,
  /** 单条登记代理渠道（查价页「同步代理渠道」）；幂等，created=false 表示已存在 */
  syncOne: (body: { agentId: string; serviceCode?: string; serviceName?: string }) =>
    kfcApi.post('/agent-services/sync-one', body) as Promise<
      AxiosResponse<{ success: boolean; message: string; service: AgentServiceChannelDTO; created: boolean }>
    >,
};

export interface CustomerAddressDTO {
  id?: string;
  label?: string;
  remark?: string;
  contactName?: string;
  companyName?: string;
  email?: string;
  phone?: string;
  address?: string;
  postalCode?: string;
}

/** 运单/预申报选址摘要（不含敏感字段） */
export interface CustomerAddressSummaryDTO {
  id?: string;
  label?: string;
  remark?: string;
}

export interface CustomerAccountDTO {
  id?: string;
  transactionJson?: string;
  payableAmount?: number;
  paidAmount?: number;
}

export interface CustomerDTO {
  id?: string;
  customerCode: string;
  domain?: string;
  name: string;
  email?: string;
  phone?: string;
  createdAt?: string;
  updatedAt?: string;
  addresses?: CustomerAddressDTO[];
  account?: CustomerAccountDTO;
  kccAccount?: CustomerKccAccountSummaryDTO;
}

/** 客户 KCC 登录账号摘要（不含密码） */
export interface CustomerKccAccountSummaryDTO {
  opened: boolean;
  loginEmail?: string;
  status?: string;
  createdAt?: string;
  passwordKnown?: boolean;
}

/** 创建 KCC 账号时填写的登录用户姓名 */
export interface CreateKccAccountRequest {
  firstName: string;
  lastName: string;
}

/** 开通 / 查看 / 重置后返回的 KCC 登录凭据 */
export interface KccAccountCredentialDTO {
  loginEmail: string;
  password: string;
  portalUrl: string;
}

export interface CustomerImportSummaryDTO {
  create: number;
  appendAddress: number;
  unchanged: number;
  error: number;
}

export interface CustomerImportAppendItemDTO {
  customerId?: string;
  customerCode?: string;
  name?: string;
  addresses?: CustomerAddressDTO[];
}

export interface CustomerImportErrorDTO {
  row: number;
  message: string;
}

export interface CustomerImportPreviewDTO {
  summary: CustomerImportSummaryDTO;
  toCreate: CustomerDTO[];
  toAppendAddress: CustomerImportAppendItemDTO[];
  errors: CustomerImportErrorDTO[];
}

export interface CustomerImportConfirmResultDTO {
  created: number;
  appendedAddress: number;
}

export const customerApi = {
  list: () => kfcApi.get('/customers') as Promise<AxiosResponse<CustomerDTO[]>>,
  get: (id: string) => kfcApi.get(`/customers/${id}`) as Promise<AxiosResponse<CustomerDTO>>,
  getByCode: (customerCode: string) =>
    kfcApi.get(`/customers/by-code/${encodeURIComponent(customerCode)}`) as Promise<AxiosResponse<CustomerDTO>>,
  listAddressSummaries: (customerCode: string) =>
    kfcApi.get(`/customers/by-code/${encodeURIComponent(customerCode)}/addresses`) as Promise<
      AxiosResponse<CustomerAddressSummaryDTO[]>
    >,
  getAddressDetail: (id: string) =>
    kfcApi.get(`/customers/addresses/${encodeURIComponent(id)}`) as Promise<AxiosResponse<CustomerAddressDTO>>,
  create: (dto: Partial<CustomerDTO>) => kfcApi.post('/customers', dto) as Promise<AxiosResponse<CustomerDTO>>,
  update: (id: string, dto: Partial<CustomerDTO>) =>
    kfcApi.put(`/customers/${id}`, dto) as Promise<AxiosResponse<CustomerDTO>>,
  delete: (id: string) => kfcApi.delete(`/customers/${id}`),
  importPreview: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return kfcApi.post('/customers/import/preview', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    }) as Promise<AxiosResponse<CustomerImportPreviewDTO>>;
  },
  importConfirm: (body: {
    toCreate: CustomerDTO[];
    toAppendAddress: CustomerImportAppendItemDTO[];
  }) =>
    kfcApi.post('/customers/import/confirm', body, {
      timeout: 120000,
    }) as Promise<AxiosResponse<CustomerImportConfirmResultDTO>>,
  downloadTemplate: () =>
    kfcApi.get('/customers/import/template', { responseType: 'blob', timeout: 30000 }),
  getKccAccount: (id: string) =>
    kfcApi.get(`/customers/${id}/kcc-account`) as Promise<AxiosResponse<CustomerKccAccountSummaryDTO>>,
  createKccAccount: (id: string, body: CreateKccAccountRequest) =>
    kfcApi.post(`/customers/${id}/kcc-account`, body, { timeout: 30000 }) as Promise<
      AxiosResponse<KccAccountCredentialDTO>
    >,
  getKccPassword: (id: string) =>
    kfcApi.get(`/customers/${id}/kcc-account/password`) as Promise<AxiosResponse<KccAccountCredentialDTO>>,
  resetKccPassword: (id: string) =>
    kfcApi.post(`/customers/${id}/kcc-account/reset-password`, null, { timeout: 30000 }) as Promise<
      AxiosResponse<KccAccountCredentialDTO>
    >,
};

/** 供应商联系人 */
export interface SupplierContactDTO {
  id?: string;
  contactName?: string;
  phoneNumber?: string;
  remark?: string;
}

/** 供应商（履行中心供应商管理） */
export interface SupplierDTO {
  id?: string;
  supplierId?: string;
  supplierName?: string;
  address?: string;
  contacts?: SupplierContactDTO[];
  createdAt?: string;
}

export interface SupplierImportAppendItemDTO {
  id?: string;
  supplierId?: string;
  supplierName?: string;
  contacts?: SupplierContactDTO[];
}

export interface SupplierImportSummaryDTO {
  create: number;
  appendContact: number;
  unchanged: number;
  error: number;
}

export interface SupplierImportErrorDTO {
  row: number;
  message: string;
}

export interface SupplierImportPreviewDTO {
  summary: SupplierImportSummaryDTO;
  toCreate: SupplierDTO[];
  toAppendContact: SupplierImportAppendItemDTO[];
  errors: SupplierImportErrorDTO[];
}

export interface SupplierImportConfirmResultDTO {
  created: number;
  appendedContact: number;
}

export const supplierApi = {
  list: () => kfcApi.get('/suppliers') as Promise<AxiosResponse<SupplierDTO[]>>,
  get: (id: string) => kfcApi.get(`/suppliers/${id}`) as Promise<AxiosResponse<SupplierDTO>>,
  getByCode: (supplierId: string) =>
    kfcApi.get(`/suppliers/by-code/${encodeURIComponent(supplierId)}`) as Promise<AxiosResponse<SupplierDTO>>,
  create: (dto: Partial<SupplierDTO>) => kfcApi.post('/suppliers', dto) as Promise<AxiosResponse<SupplierDTO>>,
  update: (id: string, dto: Partial<SupplierDTO>) =>
    kfcApi.put(`/suppliers/${id}`, dto) as Promise<AxiosResponse<SupplierDTO>>,
  delete: (id: string) => kfcApi.delete(`/suppliers/${id}`),
  importPreview: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return kfcApi.post('/suppliers/import/preview', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    }) as Promise<AxiosResponse<SupplierImportPreviewDTO>>;
  },
  importConfirm: (body: { toCreate: SupplierDTO[]; toAppendContact?: SupplierImportAppendItemDTO[] }) =>
    kfcApi.post('/suppliers/import/confirm', body, { timeout: 120000 }) as Promise<
      AxiosResponse<SupplierImportConfirmResultDTO>
    >,
  downloadTemplate: () =>
    kfcApi.get('/suppliers/import/template', { responseType: 'blob', timeout: 30000 }),
};

/** 产品（设计文档 Product：海关编码、名称、品牌、图片、用途、材质、价格） */
export interface ProductDTO {
  id?: string;
  hscode?: string;
  nameZN?: string;
  nameEN?: string;
  brand?: string;
  model?: string;
  picture?: string;
  containsBattery?: string;
  containsMagnet?: string;
  usage?: { zh_CN?: string; en_US?: string };
  material?: { zh_CN?: string; en_US?: string };
  price?: { USD?: number; RMB?: number };
}

export interface ProductImportSummaryDTO {
  create: number;
  update: number;
  unchanged: number;
  error: number;
}

export interface ProductImportUpdateItemDTO {
  before: ProductDTO;
  after: ProductDTO;
}

export interface ProductImportErrorDTO {
  row: number;
  message: string;
}

export interface ProductImportPreviewDTO {
  summary: ProductImportSummaryDTO;
  toCreate: ProductDTO[];
  toUpdate: ProductImportUpdateItemDTO[];
  errors: ProductImportErrorDTO[];
}

export interface ProductImportConfirmResultDTO {
  created: number;
  updated: number;
}

export function productKey(nameEN?: string, hscode?: string): string {
  return `${(nameEN || '').trim()}::${(hscode || '').trim()}`;
}

export function parseProductKey(key: string): { nameEN: string; hscode: string } {
  const idx = key.indexOf('::');
  if (idx < 0) return { nameEN: '', hscode: key.trim() };
  return { nameEN: key.slice(0, idx).trim(), hscode: key.slice(idx + 2).trim() };
}

export const productApi = {
  list: (params?: { view?: 'summary' | string }) =>
    kfcApi.get('/products', { params: params ?? {} }) as Promise<AxiosResponse<ProductDTO[]>>,
  get: (id: string) => kfcApi.get(`/products/${id}`) as Promise<AxiosResponse<ProductDTO>>,
  getByHscode: (hscode: string) =>
    kfcApi.get(`/products/by-hscode/${encodeURIComponent(hscode)}`) as Promise<AxiosResponse<ProductDTO>>,
  getByKey: (nameEN: string, hscode: string) =>
    kfcApi.get('/products/by-key', { params: { nameEN, hscode } }) as Promise<AxiosResponse<ProductDTO>>,
  create: (dto: Partial<ProductDTO>) => kfcApi.post('/products', dto) as Promise<AxiosResponse<ProductDTO>>,
  update: (id: string, dto: Partial<ProductDTO>) =>
    kfcApi.put(`/products/${id}`, dto) as Promise<AxiosResponse<ProductDTO>>,
  delete: (id: string) => kfcApi.delete(`/products/${id}`),
  importPreview: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return kfcApi.post('/products/import/preview', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    }) as Promise<AxiosResponse<ProductImportPreviewDTO>>;
  },
  importConfirm: (body: { toCreate: ProductDTO[]; toUpdate: ProductDTO[] }) =>
    kfcApi.post('/products/import/confirm', body, { timeout: 120000 }) as Promise<AxiosResponse<ProductImportConfirmResultDTO>>,
  downloadTemplate: () =>
    kfcApi.get('/products/import/template', { responseType: 'blob', timeout: 30000 }),
};

/** 货物（设计文档 Carton） */
export interface CartonProductItemDTO {
  hscode?: string;
  nameEN?: string;
  amount?: number;
}

export interface CartonDTO {
  id?: string;
  cartonId?: string;
  fromSupplierId?: string;
  belongClientId?: string;
  products?: CartonProductItemDTO[];
  state?: string;
  states?: Record<string, Record<string, unknown>>;
  shipmentId?: string;
  batchId?: string;
  batchRemark?: string;
  measuredWeightKg?: number;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
  billingWeightKg?: number;
  createTimestamp?: number;
}

export interface CreateCartonRequest {
  cartonId: string;
  fromSupplierId?: string;
  belongClientId?: string;
  products?: CartonProductItemDTO[];
}

/** 批次录入中的单箱信息 */
export interface CreateCartonItemInBatch {
  cartonId: string;
  products?: CartonProductItemDTO[];
}

/** 批次录入请求：cartons 为空只建批次；shipmentId 非空时创建即绑定该运单 */
export interface CreateCartonBatchRequest {
  batchId: string;
  boxCount: number;
  fromSupplierId: string;
  belongClientId: string;
  batchRemark?: string;
  /** 货物品名 */
  cargoName?: string;
  cartons?: CreateCartonItemInBatch[];
  shipmentId?: string;
}

/** 货物批次 */
export interface ShipmentPackingListDTO {
  id: string;
  shipmentId: string;
  scope: 'SHIPMENT' | 'BATCH' | string;
  batchId?: string;
  fileName: string;
  contentType?: string;
  generatedAt?: string;
  generatedBy?: string;
  generatedByName?: string;
}

export interface CartonBatchDTO {
  id?: string;
  batchId?: string;
  pieceCount?: number;
  /** 已生成单箱数 */
  materializedCount?: number;
  fromSupplierId?: string;
  belongClientId?: string;
  batchRemark?: string;
  /** 货物品名 */
  cargoName?: string;
  shipmentId?: string;
  perBoxEntry?: boolean;
  source?: string;
  createTimestamp?: number;
  updateTimestamp?: number;
  cartons?: CartonDTO[];
}

export interface UpdateCartonBatchRequest {
  pieceCount?: number;
  fromSupplierId?: string;
  belongClientId?: string;
  batchRemark?: string;
  /** 货物品名；传空字符串可清空 */
  cargoName?: string;
}

export const cartonBatchApi = {
  list: (params?: {
    batchIdKeyword?: string;
    belongClientId?: string | string[];
    forBind?: boolean;
    bindShipmentId?: string;
    shipmentId?: string;
    page?: number;
    size?: number;
  }) =>
    kfcApi.get('/carton-batches', { params: params ?? {} }) as Promise<
      AxiosResponse<{ content: CartonBatchDTO[]; totalElements: number }>
    >,
  get: (id: string) => kfcApi.get(`/carton-batches/${id}`) as Promise<AxiosResponse<CartonBatchDTO>>,
  create: (body: CreateCartonBatchRequest) =>
    kfcApi.post('/carton-batches', body) as Promise<AxiosResponse<CartonBatchDTO>>,
  update: (id: string, body: UpdateCartonBatchRequest) =>
    kfcApi.put(`/carton-batches/${id}`, body) as Promise<AxiosResponse<CartonBatchDTO>>,
  remove: (id: string) => kfcApi.delete(`/carton-batches/${id}`),
  bindShipment: (body: { batchIds: string[]; shipmentId: string }) =>
    kfcApi.post('/carton-batches/bind-shipment', body) as Promise<AxiosResponse<CartonBatchDTO[]>>,
  unbindShipment: (body: { batchIds: string[] }) =>
    kfcApi.post('/carton-batches/unbind-shipment', body) as Promise<AxiosResponse<CartonBatchDTO[]>>,
};

/** 更新货物请求（详情编辑；不含运单号，运单请用 bindShipment） */
export interface UpdateCartonRequest {
  cartonId?: string;
  fromSupplierId?: string;
  belongClientId?: string;
  products?: CartonProductItemDTO[];
  measuredWeightKg?: number;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
  billingWeightKg?: number;
}

/** 批量更新供应商 */
export interface BatchUpdateSupplierRequest {
  cartonIds: string[];
  fromSupplierId: string;
}

/** 将多箱绑定到运单 */
export interface BindCartonsToShipmentRequest {
  cartonIds: string[];
  shipmentId: string;
}

/** 收货请求：到达方式、查询单号、照片（标注 -> base64） */
export interface ReceiveCartonRequest {
  arrivedBy: number; // 0 货拉拉，1 快递
  queryParamOfShippment?: string;
  pictures?: Record<string, string>;
}

/** 批量收货请求 */
export interface ReceiveCartonBatchRequest {
  cartonIds: string[];
  arrivedBy: number;
  queryParamOfShippment?: string;
  pictures?: Record<string, string>;
}

export const cartonApi = {
  list: (params?: {
    shipmentId?: string;
    belongClientId?: string | string[];
    forBind?: boolean;
    bindShipmentId?: string;
    cartonIdKeyword?: string;
    shipmentIdKeyword?: string;
    page?: number;
    size?: number;
  }) =>
    kfcApi.get('/cartons', { params: params ?? {} }) as Promise<
      AxiosResponse<CartonDTO[] | { content: CartonDTO[]; totalElements: number }>
    >,
  /** 解析 list 响应：无 page 时为数组，有 page 时为 Spring Page */
  unwrapList: (data: CartonDTO[] | { content: CartonDTO[]; totalElements: number } | undefined): CartonDTO[] => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return data.content || [];
  },
  get: (id: string) => kfcApi.get(`/cartons/${id}`) as Promise<AxiosResponse<CartonDTO>>,
  createCartons: (body: { cartons: CreateCartonRequest[] }) =>
    kfcApi.post('/cartons', body) as Promise<AxiosResponse<CartonDTO[]>>,
  createCartonBatch: (body: CreateCartonBatchRequest) =>
    kfcApi.post('/cartons/batch', body) as Promise<AxiosResponse<CartonBatchDTO>>,
  update: (id: string, body: UpdateCartonRequest) =>
    kfcApi.put(`/cartons/${id}`, body) as Promise<AxiosResponse<CartonDTO>>,
  batchUpdateSupplier: (body: BatchUpdateSupplierRequest) =>
    kfcApi.post('/cartons/batch-update-supplier', body) as Promise<AxiosResponse<CartonDTO[]>>,
  bindShipment: (body: BindCartonsToShipmentRequest) =>
    kfcApi.post('/cartons/bind-shipment', body) as Promise<AxiosResponse<CartonDTO[]>>,
  receive: (id: string, body: ReceiveCartonRequest) =>
    kfcApi.post(`/cartons/${id}/receive`, body) as Promise<AxiosResponse<CartonDTO>>,
  receiveBatch: (body: ReceiveCartonBatchRequest) =>
    kfcApi.post('/cartons/receive', body) as Promise<AxiosResponse<CartonDTO[]>>,
};

/** 操作日志 */
export interface OperationLogDTO {
  id?: string;
  loginName?: string;
  identityId?: string;
  displayName?: string;
  operatedAt?: string;
  module?: string;
  resourceType?: string;
  resourceId?: string;
  action?: string;
  actionLabel?: string;
  summary?: string;
  requestMethod?: string;
  requestPath?: string;
  result?: string;
  errorMessage?: string;
  clientIp?: string;
  userAgent?: string;
  durationMs?: number;
  details?: Record<string, unknown>;
}

export interface OperationLogPage {
  data: OperationLogDTO[];
  page: number;
  per_page: number;
  total: number;
}

export const operationLogApi = {
  list: (params?: {
    page?: number;
    perPage?: number;
    loginName?: string;
    module?: string;
    action?: string;
    result?: string;
    startTime?: string;
    endTime?: string;
    resourceType?: string;
    resourceId?: string;
  }) =>
    kfcApi.get('/operation-logs', { params: params ?? {} }) as Promise<AxiosResponse<OperationLogPage>>,
};

export interface DashboardMetricsDTO {
  sampleCount: number;
  avgDays?: number | null;
  maxDays?: number | null;
  minDays?: number | null;
  p90Days?: number | null;
}

export interface DashboardMonitorDTO {
  problemCount: number;
  overThresholdCount: number;
}

export interface DashboardShipmentRowDTO {
  shipmentId: string;
  clientCode?: string;
  agentIdentifier?: string;
  status: string;
  statusLabel?: string;
  statusChangedAt?: string;
  dwellDays?: number | null;
  thresholdDays?: number | null;
  alert?: boolean;
  problemStatus?: string;
  problemMarkedAt?: string;
}

export const shipmentDashboardApi = {
  metrics: (params?: { from?: string; to?: string }) =>
    kfcApi.get('/shipments/dashboard/metrics', { params: params ?? {} }) as Promise<AxiosResponse<DashboardMetricsDTO>>,
  monitor: () =>
    kfcApi.get('/shipments/dashboard/monitor') as Promise<AxiosResponse<DashboardMonitorDTO>>,
  shipments: (params: {
    kind: 'PROBLEM' | 'OVER_THRESHOLD';
    page?: number;
    size?: number;
  }) =>
    kfcApi.get('/shipments/dashboard/shipments', { params }) as Promise<
      AxiosResponse<{ content: DashboardShipmentRowDTO[]; totalElements: number }>
    >,
};

export interface ShipmentSlaThresholdsDTO {
  thresholdsByStatus: Record<string, number>;
}

export const shipmentSlaThresholdApi = {
  get: () =>
    kfcApi.get('/system/shipment-sla-thresholds') as Promise<AxiosResponse<ShipmentSlaThresholdsDTO>>,
  put: (dto: ShipmentSlaThresholdsDTO) =>
    kfcApi.put('/system/shipment-sla-thresholds', dto) as Promise<AxiosResponse<ShipmentSlaThresholdsDTO>>,
};

export interface ShipxyConfigDTO {
  configured: boolean;
  /** GET 时为脱敏密钥（****尾四位）；PUT 提交明文，留空不修改 */
  apiKey?: string | null;
}

export const shipxyConfigApi = {
  get: () =>
    kfcApi.get('/system/shipxy-config') as Promise<AxiosResponse<ShipxyConfigDTO>>,
  put: (dto: ShipxyConfigDTO) =>
    kfcApi.put('/system/shipxy-config', dto) as Promise<AxiosResponse<ShipxyConfigDTO>>,
};

export interface LlmProviderDTO {
  id: string;
  name: string;
  providerType?: string;
  modelId?: string;
  apiKey?: string;
  apiSecret?: string;
  apiBase?: string;
  supportsVision?: boolean;
  reasoningEffort?: 'low' | 'high' | 'max' | '';
  enabled?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export const llmProviderApi = {
  list: () => kfcApi.get('/llm-providers') as Promise<AxiosResponse<LlmProviderDTO[]>>,
  create: (dto: Partial<LlmProviderDTO>) =>
    kfcApi.post('/llm-providers', dto) as Promise<AxiosResponse<LlmProviderDTO>>,
  update: (id: string, dto: Partial<LlmProviderDTO>) =>
    kfcApi.put(`/llm-providers/${id}`, dto) as Promise<AxiosResponse<LlmProviderDTO>>,
  delete: (id: string) => kfcApi.delete(`/llm-providers/${id}`),
  enable: (id: string) =>
    kfcApi.post(`/llm-providers/${id}/enable`) as Promise<AxiosResponse<LlmProviderDTO>>,
};

export interface BillingWeightRuleDTO {
  id: string;
  name: string;
  ruleText: string;
  compiledFormula?: string;
  formulaVersion?: number;
  compileStatus?: string;
  compileError?: string;
  compiledAt?: string;
  cascadeRecalcCount?: number;
  updatedAt?: string;
  createdAt?: string;
}

export interface BillingWeightCalcResultDTO {
  shipmentId: string;
  suggestedWeightKg?: number;
  processSteps?: string[];
  ruleId?: string;
  ruleName?: string;
  ruleText?: string;
  formulaVersion?: number;
  missingInput?: boolean;
  stale?: boolean;
}

export const billingWeightRuleApi = {
  list: () => kfcApi.get('/billing-weight-rules') as Promise<AxiosResponse<BillingWeightRuleDTO[]>>,
  create: (dto: Partial<BillingWeightRuleDTO>) =>
    kfcApi.post('/billing-weight-rules', dto) as Promise<AxiosResponse<BillingWeightRuleDTO>>,
  update: (id: string, dto: Partial<BillingWeightRuleDTO>) =>
    kfcApi.put(`/billing-weight-rules/${id}`, dto) as Promise<AxiosResponse<BillingWeightRuleDTO>>,
  delete: (id: string) => kfcApi.delete(`/billing-weight-rules/${id}`),
};

// ---------------------------------------------------------------- 代理查价

export type AgentPriceSource = 'EXCEL' | 'API_SAMPLING' | 'LIVE' | 'LIVE_CACHE';
export type KfcChannelStatus = 'CREATED' | 'NOT_CREATED' | 'SERVICE_NOT_SYNCED';

export interface AgentPricePiece {
  weight?: number;
  length?: number;
  width?: number;
  height?: number;
  count?: number;
}

export interface AgentPriceQueryRequest {
  countryCode: string;
  pcs: number;
  /** 总实重 KG */
  weight: number;
  /** 总体积 CBM */
  volume?: number;
  packing: string;
  goodsTypes?: string[];
  /** 代理标识；空表示全部 */
  agentIds?: string[];
  queryDate?: string;
  postcode?: string;
  city?: string;
  state?: string;
  markupFixed?: number;
  markupPercent?: number;
  pieces?: AgentPricePiece[];
}

export interface AgentPriceFeeDTO {
  code?: string;
  name?: string;
  amount?: number;
  currency?: string;
  amountCny?: number;
  formula?: string;
}

export interface AgentPriceRowDTO {
  source: AgentPriceSource;
  agentId: string;
  agentName?: string;
  agentChannelCode?: string;
  channelName?: string;
  countryCode?: string;
  destName?: string;
  chargeWeight?: number;
  unitPriceCny?: number;
  totalChargeCny?: number;
  markupCny?: number;
  freightCny?: number;
  otherFeeCny?: number;
  freightOrig?: number;
  currency?: string;
  formula?: string;
  volumeDivisor?: number;
  transitTime?: string;
  runTimeMs?: number;
  remark?: string;
  zoneCode?: string;
  otherFees?: AgentPriceFeeDTO[];
  missingRate?: boolean;
  sheetId?: string;
  effectiveDate?: string;
  /** LIVE_CACHE 行的报价时间 */
  quoteTime?: string;
  estimated?: boolean;
  kfcChannelStatus?: KfcChannelStatus;
  kfcChannelId?: string;
  kfcChannelName?: string;
  serviceId?: number;
  suggestedChannelCode?: string;
  carrierId?: string;
  carrierName?: string;
}

export interface AgentPriceAgentStatusDTO {
  agentId: string;
  agentName?: string;
  success: boolean;
  count: number;
  message?: string;
  runTimeMs?: number;
}

export interface AgentPriceQueryResultDTO {
  rows: AgentPriceRowDTO[];
  agents: AgentPriceAgentStatusDTO[];
}

export interface AgentPriceAgentOption {
  agentId: string;
  agentName: string;
  supportsQuote: boolean;
}

export interface AgentPriceSheetDTO {
  id: string;
  agentId: string;
  agentName?: string;
  agentChannelCode: string;
  channelName?: string;
  effectiveDate: string;
  currency?: string;
  volumeDivisor?: number;
  transitTime?: string;
  remark?: string;
  source: 'EXCEL' | 'API_SAMPLING';
  importBatchId?: string;
  status: 'ACTIVE' | 'SUPERSEDED';
  itemCount?: number;
  createdAt?: string;
  createdBy?: string;
}

export interface AgentPriceSheetPage {
  data: AgentPriceSheetDTO[];
  page: number;
  per_page: number;
  total: number;
}

export interface AgentPriceOtherFee {
  code?: string;
  name?: string;
  mode?: 'PER_TICKET' | 'PER_KG';
  amount?: number;
}

export interface AgentPriceItemDTO {
  id: string;
  countryCode?: string;
  zoneCode?: string;
  packing?: string;
  goodsTypes?: string[];
  weightMin?: number;
  weightMax?: number;
  pricingMode?: 'TOTAL' | 'PER_KG' | 'FIRST_ADDITIONAL';
  totalPrice?: number;
  unitPrice?: number;
  firstWeight?: number;
  firstPrice?: number;
  additionalUnit?: number;
  additionalPrice?: number;
  minCharge?: number;
  fuelRate?: number;
  otherFees?: AgentPriceOtherFee[];
  estimated?: boolean;
}

export interface AgentPriceRowMessage {
  row: number;
  message: string;
}

export interface AgentPriceImportPreviewDTO {
  previewId?: string;
  fileName?: string;
  sheetCount: number;
  itemCount: number;
  supersedeCount: number;
  sheets: {
    agentId: string;
    agentChannelCode: string;
    channelName?: string;
    effectiveDate: string;
    currency?: string;
    itemCount: number;
    replaceExisting: boolean;
  }[];
  errors: AgentPriceRowMessage[];
  warnings: AgentPriceRowMessage[];
}

export interface AgentPriceImportBatchDTO {
  id: string;
  fileName?: string;
  sheetCount?: number;
  itemCount?: number;
  supersededCount?: number;
  createdBy?: string;
  createdAt?: string;
}

export interface AgentPriceSamplingRunSummary {
  agentId?: string;
  calls?: number;
  failedCalls?: number;
  quotes?: number;
  sheets?: number;
  skippedExcelChannels?: number;
  message?: string;
}

export interface AgentPriceSamplingTaskDTO {
  id?: string;
  name: string;
  agentIds?: string[];
  countryCodes: string[];
  weightPoints: number[];
  packing?: string;
  goodsTypes?: string[];
  enabled?: boolean;
  lastRunAt?: string;
  lastRunStatus?: 'RUNNING' | 'SUCCESS' | 'PARTIAL' | 'FAILED';
  lastRunSummary?: AgentPriceSamplingRunSummary[];
  createdAt?: string;
  updatedAt?: string;
}

export interface ExchangeRateDTO {
  id?: string;
  currency: string;
  rateToCny: number;
  effectiveDate: string;
  remark?: string;
  updatedAt?: string;
}

export interface AgentPriceTrendRequest {
  dimension: 'CHANNEL' | 'CARRIER';
  /** agentId::agentChannelCode */
  channelKeys?: string[];
  /** 承运商 id；UNCLASSIFIED 表示未分类 */
  carrierIds?: string[];
  countryCode: string;
  weight?: number;
  packing?: string;
  from?: string;
  to?: string;
  includeQuotes?: boolean;
}

export interface AgentPriceTrendDTO {
  series: { key: string; name: string; points: { date: string; value: number; source?: string }[] }[];
  quotes: { key: string; name: string; time: string; value: number; source: string }[];
}

export interface AgentPriceChannelOption {
  key: string;
  agentId: string;
  agentName: string;
  agentChannelCode: string;
  channelName?: string;
  carrierId: string;
  carrierName: string;
  /** SHEET（价目表）/ QUOTE（报价快照） */
  sources?: string[];
}

export interface AgentPriceSheetFilter {
  agentId?: string;
  keyword?: string;
  source?: string;
  status?: string;
  from?: string;
  to?: string;
}

export const agentPriceApi = {
  query: (req: AgentPriceQueryRequest) =>
    kfcApi.post('/agent-prices/query', req, { timeout: 60000 }) as Promise<AxiosResponse<AgentPriceQueryResultDTO>>,
  agents: () => kfcApi.get('/agent-prices/agents') as Promise<AxiosResponse<AgentPriceAgentOption[]>>,
  trend: (req: AgentPriceTrendRequest) =>
    kfcApi.post('/agent-prices/trend', req, { timeout: 60000 }) as Promise<AxiosResponse<AgentPriceTrendDTO>>,
  trendChannels: () =>
    kfcApi.get('/agent-prices/trend/channels') as Promise<AxiosResponse<AgentPriceChannelOption[]>>,
  sheets: (filter: AgentPriceSheetFilter & { page: number; per_page: number }) =>
    kfcApi.get('/agent-prices/sheets', { params: filter }) as Promise<AxiosResponse<AgentPriceSheetPage>>,
  sheetItems: (id: string) =>
    kfcApi.get(`/agent-prices/sheets/${id}/items`) as Promise<AxiosResponse<AgentPriceItemDTO[]>>,
  supersedeSheet: (id: string) =>
    kfcApi.put(`/agent-prices/sheets/${id}/supersede`) as Promise<AxiosResponse<AgentPriceSheetDTO>>,
  exportSheets: (filter: AgentPriceSheetFilter) =>
    kfcApi.get('/agent-prices/sheets/export', { params: filter, responseType: 'blob', timeout: 120000 }),
  downloadTemplate: () =>
    kfcApi.get('/agent-prices/import/template', { responseType: 'blob', timeout: 30000 }),
  importPreview: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return kfcApi.post('/agent-prices/import/preview', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    }) as Promise<AxiosResponse<AgentPriceImportPreviewDTO>>;
  },
  importConfirm: (previewId: string) =>
    kfcApi.post('/agent-prices/import/confirm', { previewId }, { timeout: 120000 }) as Promise<
      AxiosResponse<AgentPriceImportBatchDTO>
    >,
  importBatches: () =>
    kfcApi.get('/agent-prices/import/batches') as Promise<AxiosResponse<AgentPriceImportBatchDTO[]>>,
  samplingTasks: () =>
    kfcApi.get('/agent-prices/sampling-tasks') as Promise<AxiosResponse<AgentPriceSamplingTaskDTO[]>>,
  createSamplingTask: (dto: AgentPriceSamplingTaskDTO) =>
    kfcApi.post('/agent-prices/sampling-tasks', dto) as Promise<AxiosResponse<AgentPriceSamplingTaskDTO>>,
  updateSamplingTask: (id: string, dto: AgentPriceSamplingTaskDTO) =>
    kfcApi.put(`/agent-prices/sampling-tasks/${id}`, dto) as Promise<AxiosResponse<AgentPriceSamplingTaskDTO>>,
  deleteSamplingTask: (id: string) => kfcApi.delete(`/agent-prices/sampling-tasks/${id}`),
  runSamplingTask: (id: string) => kfcApi.post(`/agent-prices/sampling-tasks/${id}/run`),
  exchangeRates: () => kfcApi.get('/agent-prices/exchange-rates') as Promise<AxiosResponse<ExchangeRateDTO[]>>,
  createExchangeRate: (dto: ExchangeRateDTO) =>
    kfcApi.post('/agent-prices/exchange-rates', dto) as Promise<AxiosResponse<ExchangeRateDTO>>,
  updateExchangeRate: (id: string, dto: ExchangeRateDTO) =>
    kfcApi.put(`/agent-prices/exchange-rates/${id}`, dto) as Promise<AxiosResponse<ExchangeRateDTO>>,
  deleteExchangeRate: (id: string) => kfcApi.delete(`/agent-prices/exchange-rates/${id}`),
};

export interface AgentPriceLiveHandlers {
  onTotal?: (total: number, agents: { agentId: string; agentName: string }[]) => void;
  onAgentResult?: (agent: AgentPriceAgentStatusDTO, rows: AgentPriceRowDTO[]) => void;
  onDone?: () => void;
}

/**
 * 实时查询（SSE over POST）：EventSource 不支持 POST body，这里用 fetch 读取事件流。
 * 返回的 Promise 在流结束后 resolve。
 */
export async function agentPriceLive(
  req: AgentPriceQueryRequest,
  handlers: AgentPriceLiveHandlers,
  signal?: AbortSignal
): Promise<void> {
  const res = await fetch(`${baseURL}/agent-prices/live`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'X-Requested-With': 'kfy-web',
    },
    body: JSON.stringify(req),
    signal,
  });
  if (!res.ok || !res.body) {
    let msg = `实时查询失败（HTTP ${res.status}）`;
    try {
      const body = await res.json();
      if (body?.message) msg = body.message;
    } catch {
      /* 非 JSON 响应 */
    }
    if (res.status === 401) {
      invalidateSessionCache();
    }
    throw new Error(msg);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  const dispatch = (block: string) => {
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of block.split(/\r?\n/)) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
    }
    if (dataLines.length === 0) return;
    const data = JSON.parse(dataLines.join('\n'));
    if (event === 'search_total') handlers.onTotal?.(data.total, data.agents ?? []);
    else if (event === 'agent_result') handlers.onAgentResult?.(data.agent, data.rows ?? []);
    else if (event === 'search_done') handlers.onDone?.();
  };
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx = buffer.search(/\r?\n\r?\n/);
    while (idx >= 0) {
      const block = buffer.slice(0, idx);
      buffer = buffer.slice(idx).replace(/^\r?\n\r?\n/, '');
      if (block.trim()) dispatch(block);
      idx = buffer.search(/\r?\n\r?\n/);
    }
  }
  if (buffer.trim()) dispatch(buffer);
}

/** 下载 axios blob 响应为文件 */
export function saveBlob(data: BlobPart, fileName: string) {
  const url = window.URL.createObjectURL(new Blob([data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  window.URL.revokeObjectURL(url);
}
